import { Component, HostListener, Input, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router, NavigationStart } from '@angular/router';
import { CommonSettings, INCPCCPayment } from 'epay-library';
import { IConfirmationNumber } from 'src/app/features/ncp/components/creditcard/payment/ncpcredit-card-common/confirmation-number';
import { EpayExtendedServiceService } from 'src/app/helpers/services/epay-extended-service.service';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { Meta, Title } from '@angular/platform-browser';

@Component({
  selector: 'epay-creditcard-success',
  templateUrl: './creditcard-success.component.html'
})
export class CreditCardSuccessComponent implements OnInit, OnDestroy {
  @Input() creditCardPayment: INCPCCPayment = {} as INCPCCPayment;
  pagetitle = "Credit Card Payment Success";
  baseurl = CommonSettings.BaseApi;
  clientId = CommonSettings.ClientID;

  paymentDate: Date = new Date();
  confirmationId: string = '';
  totalAmount: number = 0;
  baseAmount: number = 0;
  serviceFee: number = 0;
  feePercentage: number = 0;
  transactionId: string = '';
  firstName: string = '';
  lastName: string = '';
  fullName: string = '';

  private navSubscription?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private paymentService: EpayExtendedServiceService,
    private router: Router,
    private dialog: MatDialog,
    private meta: Meta,
    private titleService: Title
  ) {}

  ngOnInit(): void {
    // Prevent bookmarking and indexing
    this.meta.addTag({ name: 'robots', content: 'noindex, nofollow, noarchive, nosnippet' });
    this.meta.addTag({ name: 'googlebot', content: 'noindex, nofollow' });
    
    sessionStorage.setItem('cameFromSuccessPage', 'true');
    this.paymentDate = new Date();
    this.firstName = sessionStorage.getItem('firstName') || '';
    this.lastName = sessionStorage.getItem('lastName') || '';
    this.fullName = [this.firstName, this.lastName].filter(Boolean).join(' ');

    this.route.queryParams.subscribe(params => {
      const confirmationNumber = params['id'];
      const transactionId = params['externalId'];
      const totalAmountParam = params['amount'];

      // Check if this is a legitimate success page visit by checking for required params
      if (!confirmationNumber && !transactionId && !totalAmountParam) {
        console.warn('[CreditCardSuccessComponent] No payment data found, redirecting to home');
        this.router.navigate(['/home'], { replaceUrl: true });
        return;
      }

      // Replace URL in browser history to prevent bookmarking
      if (window.history.replaceState) {
        window.history.replaceState(null, '', '/ND');
      }

      if (totalAmountParam) {
        this.totalAmount = parseFloat(totalAmountParam);
      }

      // Get base amount and fee info from session storage
      const storedPayment = sessionStorage.getItem('creditCardPayment');
      if (storedPayment) {
        const paymentData = JSON.parse(storedPayment);
        this.baseAmount = paymentData.amount || 0;
        this.feePercentage = paymentData.feePercentage || 0;
        const fee = this.baseAmount * this.feePercentage;
        this.serviceFee = Math.round((fee + Number.EPSILON) * 100) / 100;
        this.totalAmount = paymentData.totalAmount || +(this.baseAmount + this.serviceFee).toFixed(2);
      } else if (totalAmountParam) {
        this.paymentService.getUserPaysFees(this.baseurl).subscribe({
          next: (feeResponse: any) => {
            this.feePercentage = parseFloat(feeResponse?.creditCardConveniencePercent) || 0;
            this.baseAmount = +(this.totalAmount / (1 + this.feePercentage)).toFixed(2);
            const fee = this.baseAmount * this.feePercentage;
            this.serviceFee = Math.round((fee + Number.EPSILON) * 100) / 100;
          },
          error: (error: any) => {
            console.error('Failed to fetch fee data:', error);
            this.baseAmount = this.totalAmount;
            this.serviceFee = 0;
            this.feePercentage = 0;
          }
        });
      }

      if (confirmationNumber && transactionId) {
        this.confirmationId = confirmationNumber;
        this.transactionId = transactionId;

        const payload: IConfirmationNumber = {
          clientId: this.clientId,
          transactionId,
          confirmationNumber
        };

        this.paymentService.postUpdateConfirmationNumber(this.baseurl, payload).subscribe({
          next: (res: any) => {
            // Success handled silently
          },
          error: (err: any) => {
            console.error('Failed to update confirmation number:', err);
          }
        });
      } else {
        console.warn('Missing confirmationNumber or transactionId in query params.');
      }
    });

    // Listen for navigation events to prevent going back to iframe
    this.navSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        if (event.url.includes('/ncp/creditcard/iframe-fort-point')) {
          console.warn('[CreditCardSuccessComponent] Preventing navigation back to iframe, redirecting to /home');
          this.router.navigate(['/home'], { replaceUrl: true });
        }
      }
    });
  }

  @HostListener('window:popstate', ['$event'])
  onPopState(event: PopStateEvent): void {
    console.warn('[SuccessComponent] Back button used. Redirecting to /home');
    this.router.navigate(['/home'], { replaceUrl: true });
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    // Clear session when navigating away
    sessionStorage.removeItem('cameFromSuccessPage');
  }

  GoToHomePage() {
    this.router.navigateByUrl('home');
  }

  Print() {
    window.print();
  }

  ngOnDestroy(): void {
    this.navSubscription?.unsubscribe();
    // Remove meta tags
    this.meta.removeTag('name="robots"');
    this.meta.removeTag('name="googlebot"');
    // Clear session data
    sessionStorage.removeItem('transactionId');
    sessionStorage.removeItem('creditCardPayment');
    sessionStorage.removeItem('cameFromSuccessPage');
  }
}
